/**
 * Forms page — DSL forms (post wizard→DSL cutover).
 *
 * Admin sees: every saved DSL form + New / Edit / Grants / Delete. Authoring opens
 * the DSL editor (FormsDslPanel). User/readonly sees: the forms granted to them as
 * cards. Running a form drives it from its STORED script via the secure run-by-id
 * endpoints (FormsDslRuntime with props.formId) — never client-supplied SQL.
 */
import { createResource, createSignal, Show, For } from 'solid-js';
import { api } from '../lib/api.js';
import { currentUser } from '../lib/auth-store.js';
import { roleLabel } from '../lib/roles.js';
import { toast } from '../lib/toast.js';
import {
  listAccessibleForms, getFormPlan, deleteForm,
  getDslFormGrants, setDslFormGrants,
} from '../lib/formsdsl-api.js';
import FormsDslPanel from './FormsDslPanel.jsx';
import FormsDslRuntime from '../components/FormsDslRuntime.jsx';
import Modal from '../components/Modal.jsx';

export default function Forms() {
  const isAdmin = () => currentUser()?.role === 'admin';

  const [forms, { refetch: refetchForms }] = createResource(async () => {
    try { return (await listAccessibleForms()).forms || []; } catch { return []; }
  });

  // Authoring view (admin): null | { mode:'new' } | { mode:'edit', id }.
  const [editing, setEditing] = createSignal(null);
  // Running a form: { form, plan, connectionId, database } | null.
  const [running, setRunning] = createSignal(null);
  const [grantTarget, setGrantTarget] = createSignal(null);

  // meta.width → the run modal's card width (sm/md/lg/xl, or a raw CSS size like
  // "640px"). null = the default wide modal.
  const runFormWidth = () => {
    const w = running()?.plan?.meta?.width;
    return { sm: '520px', md: '720px', lg: '960px', xl: '1200px' }[w] || (w && w !== 'full' && /[0-9]/.test(w) ? w : null);
  };

  const openForm = async (f, params) => {
    try {
      const r = await getFormPlan(f.id);
      if ((r.plan?.diagnostics || []).length) {
        toast.error('This form has errors and cannot be run — edit it first.');
        return;
      }
      setRunning({ form: f, plan: r.plan, connectionId: r.connectionId, database: r.database, params: params || null });
    } catch (e) { toast.error(e.message || String(e)); }
  };

  // Navigate between forms via `open "Name" with { … }` — resolve the target by
  // name within the forms the user can access and open it with the passed params.
  const openByName = (name, params) => {
    const want = String(name).toLowerCase();
    const f = (forms() || []).find((x) => (x.name || '').toLowerCase() === want);
    if (!f) { toast.error(`Form “${name}” not found or not accessible.`); return; }
    openForm(f, params);
  };

  const removeForm = async (f) => {
    if (!confirm(`Delete form "${f.name}"? This cannot be undone.`)) return;
    try {
      await deleteForm(f.id);
      toast.success(`Deleted "${f.name}"`);
      refetchForms();
    } catch (e) { toast.error(e.message || String(e)); }
  };

  // Authoring takes over the page (the editor needs the room).
  return (
    <Show
      when={!editing()}
      fallback={
        <div class="forms-dsl-host">
          <FormsDslPanel
            initialFormId={editing().mode === 'edit' ? editing().id : null}
            onBack={() => { setEditing(null); refetchForms(); }}
          />
        </div>
      }
    >
      <div class="page">
        <header class="page-header">
          <div>
            <h1>Forms</h1>
            <p class="muted">
              {isAdmin()
                ? 'Author data-entry forms in the DSL editor; run them here.'
                : 'Data-entry forms available to you.'}
            </p>
          </div>
          <Show when={isAdmin()}>
            <button class="btn" onClick={() => setEditing({ mode: 'new' })}>+ New Form</button>
          </Show>
        </header>

        <Show when={forms()} fallback={<div class="muted">Loading…</div>}>
          <Show
            when={forms().length > 0}
            fallback={
              <div class="empty-state">
                <Show
                  when={isAdmin()}
                  fallback={<><h2>No forms available</h2><p class="muted">Ask an admin to grant you access to a form.</p></>}
                >
                  <h2>No forms yet</h2>
                  <p class="muted">Create your first DSL form to enable safe data entry.</p>
                  <button class="btn" onClick={() => setEditing({ mode: 'new' })}>+ New Form</button>
                </Show>
              </div>
            }
          >
            <div class="forms-grid">
              <For each={forms()}>
                {(f) => (
                  <div class="form-card">
                    <header>
                      <div>
                        <h3>{f.name}</h3>
                        <Show when={f.description}>
                          <div class="form-card-meta muted">{f.description}</div>
                        </Show>
                      </div>
                    </header>
                    <footer>
                      <button class="btn" onClick={() => openForm(f)}>Open</button>
                      <Show when={isAdmin()}>
                        <button class="btn btn-ghost" onClick={() => setEditing({ mode: 'edit', id: f.id })}>Edit</button>
                        <button class="btn btn-ghost" onClick={() => setGrantTarget(f)}>Grants</button>
                        <button class="btn btn-ghost btn-danger" onClick={() => removeForm(f)}>Delete</button>
                      </Show>
                    </footer>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </Show>

        {/* Run a form (all granted users) — driven by its stored script. */}
        <Show when={running()}>
          <Modal open={true} onClose={() => setRunning(null)} title={running().form.name}
                 wide={!runFormWidth()} cardStyle={runFormWidth() ? { 'max-width': runFormWidth(), width: '100%' } : undefined}>
            {/* Keyed: each run (incl. `open "B" with { … }` from inside a form) mounts
                a FRESH runtime — it seeds params/values once at creation, so reusing
                the previous instance would skip B's params and leak A's values. */}
            <Show when={running()} keyed>
              {(r) => (
                <FormsDslRuntime
                  formId={r.form.id}
                  plan={r.plan}
                  connectionId={r.connectionId}
                  database={r.database}
                  params={r.params}
                  onOpen={openByName}
                  onClose={() => setRunning(null)}
                />
              )}
            </Show>
          </Modal>
        </Show>

        {/* Grant manager (admin) */}
        <Show when={grantTarget()}>
          <FormGrantModal form={grantTarget()} onClose={() => setGrantTarget(null)} />
        </Show>
      </div>
    </Show>
  );
}

// ── Form Grants Modal (DSL forms) ───────────────────────────────────────────────

function FormGrantModal(props) {
  const [users] = createResource(async () => {
    const r = await api.get('/api/users');
    return (r.users || []).filter((u) => u.role !== 'admin'); // admins always have access
  });

  const [grants, setGrants] = createSignal(new Set());
  const [loaded, setLoaded] = createSignal(false);
  const [saving, setSaving] = createSignal(false);

  // Load which users currently have this form granted.
  createResource(
    () => (users() ? props.form.id : null),
    async () => {
      const usrList = users() || [];
      const granted = new Set();
      await Promise.all(usrList.map(async (u) => {
        try {
          const r = await getDslFormGrants(u.id);
          if ((r.formIds || []).includes(props.form.id)) granted.add(u.id);
        } catch { /* ignore */ }
      }));
      setGrants(granted);
      setLoaded(true);
    }
  );

  const toggle = (userId) => {
    setGrants((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const usrList = users() || [];
      await Promise.all(usrList.map(async (u) => {
        const r = await getDslFormGrants(u.id);
        const current = new Set(r.formIds || []);
        if (grants().has(u.id)) current.add(props.form.id); else current.delete(props.form.id);
        await setDslFormGrants(u.id, [...current]);
      }));
      toast.success('Grants saved');
      props.onClose();
    } catch (e) {
      toast.error(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={true} onClose={props.onClose} title={`Grants: ${props.form.name}`}>
      <Show when={loaded()} fallback={<div class="muted">Loading users…</div>}>
        <p class="muted" style="margin-bottom:12px;">
          Select which users can run this form. Admin users always have access.
        </p>
        <div class="grant-user-list">
          <For each={users()}>
            {(u) => (
              <label class="checkbox-label">
                <input type="checkbox" checked={grants().has(u.id)} onChange={() => toggle(u.id)} />
                <span>{u.username}</span>
                <span class={`badge-role ${u.role}`}>{roleLabel(u.role)}</span>
              </label>
            )}
          </For>
        </div>
        <div style="margin-top:16px;display:flex;gap:8px;">
          <button class="btn" disabled={saving()} onClick={save}>{saving() ? 'Saving…' : 'Save Grants'}</button>
          <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
        </div>
      </Show>
    </Modal>
  );
}
